/**
 * Certification List - Active and Expired/Revoked Certifications View
 *
 * Displays all certifications with filtering:
 * - Active certifications (default)
 * - Expired certifications
 * - Revoked certifications
 *
 * @epic DC-523 Vendor Certification Program
 * @phase Phase 9-10: Certification Views
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

type CertificationTier = 'platinum' | 'gold' | 'silver' | 'bronze';
type CertificationStatus = 'active' | 'expiring_soon' | 'expired' | 'revoked';

interface Certification {
  certification_id: string;
  product_id: string;
  vendor_id?: string;
  vendor_name?: string;
  product_name?: string;
  tier: CertificationTier;
  cert_number: string;
  overall_score: number;
  issued_at: string;
  expires_at: string;
  gxp_approved: boolean;
  revoked_at?: string;
  revocation_reason?: string;
  status?: CertificationStatus;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TIER_COLORS: Record<CertificationTier, { bg: string; text: string; border: string }> = {
  platinum: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  gold: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  silver: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
  bronze: { bg: '#ffedd5', text: '#9a3412', border: '#fed7aa' },
};

const STATUS_LABELS: Record<CertificationStatus | 'all', string> = {
  all: 'All',
  active: 'Active',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  revoked: 'Revoked',
};

const STATUS_STYLES: Record<CertificationStatus, { bg: string; text: string }> = {
  active: { bg: '#dcfce7', text: '#166534' },
  expiring_soon: { bg: '#fef3c7', text: '#92400e' },
  expired: { bg: '#fee2e2', text: '#991b1b' },
  revoked: { bg: '#fce7f3', text: '#9d174d' },
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const TierBadge: React.FC<{ tier: CertificationTier }> = ({ tier }) => {
  const style = TIER_COLORS[tier];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '6px',
        backgroundColor: style.bg,
        color: style.text,
        border: `1px solid ${style.border}`,
        fontWeight: 600,
        fontSize: '12px',
        textTransform: 'capitalize',
      }}
    >
      {tier}
    </span>
  );
};

const StatusBadge: React.FC<{ status: CertificationStatus }> = ({ status }) => {
  const style = STATUS_STYLES[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '6px',
        backgroundColor: style.bg,
        color: style.text,
        fontSize: '12px',
        fontWeight: 500,
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const CertificationList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<CertificationStatus | 'all'>(
    (searchParams.get('status') as CertificationStatus) || 'active'
  );
  const [tierFilter, setTierFilter] = useState<CertificationTier | 'all'>(
    (searchParams.get('tier') as CertificationTier) || 'all'
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const fetchCertifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      params.append('limit', String(pageSize));
      params.append('offset', String((page - 1) * pageSize));

      const response = await fetch(`/api/certifications?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch certifications');
      }

      const data = await response.json();
      setCertifications(data.certifications || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tierFilter, page, pageSize]);

  useEffect(() => {
    fetchCertifications();
  }, [fetchCertifications]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'active') params.set('status', statusFilter);
    if (tierFilter !== 'all') params.set('tier', tierFilter);
    if (searchQuery) params.set('search', searchQuery);
    setSearchParams(params, { replace: true });
  }, [statusFilter, tierFilter, searchQuery, setSearchParams]);

  // Filter by search (client-side for now)
  const filteredCerts = searchQuery
    ? certifications.filter(
        (c) =>
          c.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.cert_number?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : certifications;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysUntilExpiry = (expiresAt: string): number => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const getCertStatus = (cert: Certification): CertificationStatus => {
    if (cert.revoked_at) return 'revoked';
    const daysLeft = getDaysUntilExpiry(cert.expires_at);
    if (daysLeft <= 0) return 'expired';
    if (daysLeft <= 30) return 'expiring_soon';
    return 'active';
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="vendor-dashboard">
      {/* Breadcrumb */}
      <nav className="vendor-breadcrumb">
        <button className="breadcrumb-link" onClick={() => navigate('/home')}>
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <button className="breadcrumb-link" onClick={() => navigate('/vendors')}>
          Vendor Management
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Certifications</span>
      </nav>

      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Certifications</h1>
          <p className="subtitle">
            View and manage all vendor certifications
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        {(['active', 'expiring_soon', 'expired', 'revoked'] as CertificationStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => {
              setStatusFilter(status);
              setPage(1);
            }}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: statusFilter === status ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'transparent',
              color: statusFilter === status ? '#3b82f6' : '#6b7280',
              fontWeight: statusFilter === status ? 600 : 400,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by vendor, product, or cert number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={tierFilter}
            onChange={(e) => {
              setTierFilter(e.target.value as CertificationTier | 'all');
              setPage(1);
            }}
          >
            <option value="all">All Tiers</option>
            <option value="platinum">Platinum</option>
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
            <option value="bronze">Bronze</option>
          </select>
        </div>
        {(tierFilter !== 'all' || searchQuery) && (
          <button
            className="btn btn-text"
            onClick={() => {
              setTierFilter('all');
              setSearchQuery('');
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading certifications...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchCertifications}>
            Try Again
          </button>
        </div>
      ) : filteredCerts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📜</div>
          <h2>No Certifications Found</h2>
          <p>
            {statusFilter === 'active'
              ? 'No active certifications. Complete vendor assessments to issue certifications.'
              : `No ${STATUS_LABELS[statusFilter].toLowerCase()} certifications found.`}
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Cert #
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Vendor / Product
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Tier
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Score
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Issued
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Expires
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Status
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCerts.map((cert) => {
                  const status = getCertStatus(cert);
                  const daysLeft = getDaysUntilExpiry(cert.expires_at);

                  return (
                    <tr
                      key={cert.certification_id}
                      style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                      onClick={() => navigate(`/vendors/${cert.vendor_id}`)}
                    >
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px' }}>
                        {cert.cert_number}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: 500 }}>{cert.vendor_name}</div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>{cert.product_name}</div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <TierBadge tier={cert.tier} />
                      </td>
                      <td style={{ padding: '12px', fontWeight: 500 }}>
                        {cert.overall_score}%
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>
                        {formatDate(cert.issued_at)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ color: daysLeft <= 30 && daysLeft > 0 ? '#dc2626' : '#6b7280' }}>
                          {formatDate(cert.expires_at)}
                        </span>
                        {status === 'active' && daysLeft > 0 && daysLeft <= 90 && (
                          <div style={{ fontSize: '12px', color: '#92400e' }}>
                            {daysLeft} days left
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <StatusBadge status={status} />
                        {cert.revocation_reason && (
                          <div style={{ fontSize: '11px', color: '#9d174d', marginTop: '4px' }}>
                            {cert.revocation_reason}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() =>
                              window.open(`/api/certifications/${cert.certification_id}/pdf`, '_blank')
                            }
                            title="Download PDF"
                          >
                            PDF
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => navigate(`/vendors/${cert.vendor_id}`)}
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container" style={{ marginTop: '24px' }}>
              <div className="pagination-info">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}{' '}
                certifications
              </div>
              <div className="pagination-controls">
                <div className="page-buttons">
                  <button
                    className="btn btn-sm"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '0 12px' }}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CertificationList;
